(() => {
  'use strict';

  const RUNTIME_KEY = 'SednaSearchSectionRuntime';
  const ROOT_SELECTOR = '[data-sedna-manual-widget]';
  const LOADER_ATTRIBUTES = [
    'data-auth-token',
    'data-container-id',
    'data-result-container-id',
    'data-base-url',
    'data-result-page',
    'data-booking-details',
    'data-back-to-search',
    'data-single-location',
  ];
  const INBOUND_PARAMS = Object.freeze([
    'queryy',
    'datestart',
    'nights',
    'pax_min',
    'pax_max',
    'pax',
  ]);
  const CLIENT_HIDDEN_ATTRIBUTE = 'data-sedna-client-hidden';
  const DEFAULT_RESULTS_PER_PAGE = 9;
  const MIN_RESULTS_PER_PAGE = 3;
  const MAX_RESULTS_PER_PAGE = 24;
  const PLACEHOLDER_IMAGE_PATTERN = /(?:^|\/)placehold\.co(?:\/|$)/i;
  const IMAGE_UNAVAILABLE_CLASS = 'sedna-search-image-unavailable';
  const NATIVE_PAGINATION_SELECTOR = [
    '.sedna-pagination',
    '.sedna-results-pagination',
    '.sedna-load-more',
    '.pagination',
    '[data-pagination]',
    '[data-sedna-pagination]',
    '[data-load-more]',
    'nav[aria-label*="pagination" i]',
  ].join(', ');

  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].initialize(document);
    return;
  }

  const instances = new Map();
  let activeRoot = null;
  let previewReloadRequested = false;
  let requiresFreshDocument = false;
  let preservedEditorRoot = null;

  const collectRoots = (scope) => {
    const roots = [];

    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    return [...new Set(roots)];
  };

  const normalizeInboundParams = () => {
    const currentUrl = new URL(window.location.href);
    const canonicalQuery = currentUrl.searchParams.get('query');
    const legacyQuery = currentUrl.searchParams.get('queryy');

    if (canonicalQuery || !legacyQuery) return false;

    currentUrl.searchParams.set('query', legacyQuery);
    window.history.replaceState(window.history.state, '', currentUrl.toString());
    return true;
  };

  const setMessage = (root, message, status) => {
    const placeholder = root.querySelector('[data-sedna-widget-message]');
    if (placeholder && message) placeholder.textContent = message;
    if (status) root.dataset.sednaStatus = status;
    root.setAttribute('aria-busy', status === 'loading' ? 'true' : 'false');
  };

  const applyWidgetCopy = (root, host) => {
    const locationPlaceholder = root.dataset.locationPlaceholder?.trim();
    const searchButtonLabel = root.dataset.searchButtonLabel?.trim();
    const locationInput = host.querySelector(
      '.sedna-search-group-location input:not([type="hidden"]), input[name="query"]:not([type="hidden"])',
    );
    const searchButtonText = host.querySelector('.sedna-search-btn span');

    if (
      locationInput &&
      locationPlaceholder &&
      locationInput.placeholder !== locationPlaceholder
    ) {
      locationInput.placeholder = locationPlaceholder;
    }

    if (
      searchButtonText &&
      searchButtonLabel &&
      searchButtonText.textContent !== searchButtonLabel
    ) {
      searchButtonText.textContent = searchButtonLabel;
    }

    if (host.querySelector('.sedna-search-bar')) {
      const instance = instances.get(root);
      if (instance) {
        if (instance.timeoutId) {
          window.clearTimeout(instance.timeoutId);
          instance.timeoutId = null;
        }
        instance.observer.disconnect();
      }
      setMessage(root, '', 'ready');
    }
  };

  const syncResultsState = (resultsHost) => {
    if (!resultsHost?.querySelector) return 'idle';

    let state = 'idle';
    if (resultsHost.querySelector('.sedna-skeleton')) state = 'loading';
    else if (resultsHost.querySelector('.sedna-error')) state = 'error';
    else if (resultsHost.querySelector('.boat-card')) state = 'ready';
    else if (
      resultsHost.querySelector('.sedna-no-results') ||
      resultsHost.querySelector('.sedna-empty-state')
    ) {
      state = 'empty';
    }

    if (resultsHost.dataset.sednaResultsState !== state) {
      resultsHost.dataset.sednaResultsState = state;
    }

    const busy = state === 'loading' ? 'true' : 'false';
    if (resultsHost.getAttribute('aria-busy') !== busy) {
      resultsHost.setAttribute('aria-busy', busy);
    }

    return state;
  };

  const hasUsableResultImageSource = (image) => {
    const source = image.getAttribute?.('src')?.trim() || '';
    const sourceSet = image.getAttribute?.('srcset')?.trim() || '';

    return Boolean(
      (source && !PLACEHOLDER_IMAGE_PATTERN.test(source)) ||
        (sourceSet && !PLACEHOLDER_IMAGE_PATTERN.test(sourceSet)),
    );
  };

  const setResultImageAvailability = (image, isAvailable) => {
    const container = image.closest?.('.boat-image-container');
    if (!container) return;

    const isMarkedUnavailable = container.classList?.contains(
      IMAGE_UNAVAILABLE_CLASS,
    );

    if (isAvailable) {
      if (image.dataset.sednaImageFallback === 'true') {
        delete image.dataset.sednaImageFallback;
      }
      if (isMarkedUnavailable) {
        container.classList.remove(IMAGE_UNAVAILABLE_CLASS);
      }
      return;
    }

    image.dataset.sednaImageFallback = 'true';
    if (!isMarkedUnavailable) {
      container.classList?.add(IMAGE_UNAVAILABLE_CLASS);
    }
  };

  const syncResultImages = (resultsHost) => {
    if (!resultsHost?.querySelectorAll) return;

    resultsHost.querySelectorAll('.boat-image-container').forEach((container) => {
      const image = container.querySelector?.('img.main-boat-img');

      if (!image) {
        if (!container.classList?.contains(IMAGE_UNAVAILABLE_CLASS)) {
          container.classList?.add(IMAGE_UNAVAILABLE_CLASS);
        }
        return;
      }

      setResultImageAvailability(image, hasUsableResultImageSource(image));
    });
  };

  const handleResultImageError = (event) => {
    const image = event.target;
    if (!image?.matches?.('img.main-boat-img')) return;

    event.preventDefault?.();
    event.stopPropagation?.();
    image.onerror = null;
    image.removeAttribute?.('onerror');
    image.removeAttribute?.('src');
    image.removeAttribute?.('srcset');
    setResultImageAvailability(image, false);
  };

  const sanitizeResultsPerPage = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (
      !Number.isInteger(parsed) ||
      parsed < MIN_RESULTS_PER_PAGE ||
      parsed > MAX_RESULTS_PER_PAGE
    ) {
      return DEFAULT_RESULTS_PER_PAGE;
    }

    return parsed;
  };

  const sameNodes = (current, next) =>
    current.length === next.length && current.every((node, index) => node === next[index]);

  const getPaginationParts = (currentPage, totalPages) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 4) return [1, 2, 3, 4, 5, null, totalPages];
    if (currentPage >= totalPages - 3) {
      return [
        1,
        null,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [1, null, currentPage - 1, currentPage, currentPage + 1, null, totalPages];
  };

  const createClientPagination = (root, resultsHost) => {
    const navigation = root.querySelector('[data-sedna-client-pagination]');
    if (!navigation) return null;

    const list = navigation.querySelector('[data-sedna-pagination-list]');
    const nextItem = navigation.querySelector('[data-sedna-pagination-next-item]');
    const previousButton = navigation.querySelector(
      '[data-sedna-pagination-action="previous"]',
    );
    const nextButton = navigation.querySelector('[data-sedna-pagination-action="next"]');
    const status = navigation.querySelector('[data-sedna-pagination-status]');

    if (!list || !nextItem || !previousButton || !nextButton || !status) return null;

    const pageSize = sanitizeResultsPerPage(navigation.dataset.pageSize);
    const pageLabelTemplate =
      navigation.dataset.pageLabelTemplate?.trim() || 'Page __PAGE__';
    const searchHost = root.querySelector('.sedna-search-widget__host');
    let cards = [];
    let currentGrid = null;
    let currentPage = 1;
    let ownedItems = [];
    const ownedHiddenCards = new Set();
    let vendorOwnsPagination = false;

    const formatPageLabel = (page) =>
      pageLabelTemplate.replace('__PAGE__', String(page));

    const revealOwnedCards = () => {
      ownedHiddenCards.forEach((card) => {
        card.hidden = false;
        card.removeAttribute?.(CLIENT_HIDDEN_ATTRIBUTE);
      });
      ownedHiddenCards.clear();
    };

    const clearPageItems = () => {
      ownedItems.forEach((item) => item.remove?.());
      ownedItems = [];
    };

    const createPageItem = (part) => {
      const item = document.createElement('li');
      item.setAttribute('data-sedna-pagination-owned', 'true');

      if (part === null) {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'pagination__item';
        ellipsis.textContent = '…';
        ellipsis.setAttribute('aria-hidden', 'true');
        item.appendChild(ellipsis);
      } else {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pagination__item motion-reduce';
        button.textContent = String(part);
        button.setAttribute('data-sedna-pagination-page', String(part));
        button.setAttribute('aria-controls', resultsHost.id);
        button.setAttribute('aria-label', formatPageLabel(part));

        if (part === currentPage) {
          button.classList.add('pagination__item--current', 'background-2');
          button.setAttribute('aria-current', 'page');
        }

        item.appendChild(button);
      }

      list.insertBefore(item, nextItem);
      ownedItems.push(item);
    };

    const renderPageItems = (totalPages) => {
      clearPageItems();
      getPaginationParts(currentPage, totalPages).forEach(createPageItem);
    };

    const applyPage = (announce = false) => {
      const totalPages = Math.ceil(cards.length / pageSize);

      if (totalPages <= 1) {
        revealOwnedCards();
        clearPageItems();
        navigation.hidden = true;
        previousButton.disabled = true;
        nextButton.disabled = true;
        status.textContent = '';
        return;
      }

      currentPage = Math.min(Math.max(currentPage, 1), totalPages);
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;

      cards.forEach((card, index) => {
        const shouldHide = index < start || index >= end;

        if (shouldHide && !card.hidden) {
          card.hidden = true;
          card.setAttribute?.(CLIENT_HIDDEN_ATTRIBUTE, 'true');
          ownedHiddenCards.add(card);
        } else if (!shouldHide && ownedHiddenCards.has(card)) {
          card.hidden = false;
          card.removeAttribute?.(CLIENT_HIDDEN_ATTRIBUTE);
          ownedHiddenCards.delete(card);
        }
      });

      navigation.hidden = false;
      previousButton.disabled = currentPage === 1;
      nextButton.disabled = currentPage === totalPages;
      renderPageItems(totalPages);
      status.textContent = announce ? formatPageLabel(currentPage) : '';
    };

    const focusResults = () => {
      resultsHost.focus?.({ preventScroll: true });
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      resultsHost.scrollIntoView?.({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    };

    const goToPage = (page) => {
      const totalPages = Math.ceil(cards.length / pageSize);
      if (totalPages <= 1) return;

      const nextPage = Math.min(Math.max(page, 1), totalPages);
      if (nextPage === currentPage) return;

      currentPage = nextPage;
      applyPage(true);
      focusResults();
    };

    const handleNavigationClick = (event) => {
      const button = event.target?.closest?.(
        '[data-sedna-pagination-action], [data-sedna-pagination-page]',
      );
      if (!button || !navigation.contains(button) || button.disabled) return;

      event.preventDefault?.();
      const action = button.getAttribute('data-sedna-pagination-action');
      if (action === 'previous') goToPage(currentPage - 1);
      else if (action === 'next') goToPage(currentPage + 1);
      else {
        const page = Number.parseInt(button.getAttribute('data-sedna-pagination-page'), 10);
        if (Number.isInteger(page)) goToPage(page);
      }
    };

    const reset = () => {
      revealOwnedCards();
      cards = [];
      currentGrid = null;
      currentPage = 1;
      clearPageItems();
      navigation.hidden = true;
      previousButton.disabled = true;
      nextButton.disabled = true;
      status.textContent = '';
    };

    const handleSearchSubmit = (event) => {
      if (!searchHost?.contains?.(event.target)) return;
      reset();
    };

    const sync = (state) => {
      if (state !== 'ready') {
        reset();
        return;
      }

      if (resultsHost.querySelector(NATIVE_PAGINATION_SELECTOR)) {
        vendorOwnsPagination = true;
      }
      if (vendorOwnsPagination) {
        reset();
        return;
      }

      const nextGrid = resultsHost.querySelector('.boat-grid');
      const hasResultsHeader = resultsHost.querySelector('.results-header');
      const nextCards = Array.from(nextGrid?.children || []).filter(
        (child) =>
          child.matches?.('.boat-card') &&
          (!child.hidden || ownedHiddenCards.has(child)),
      );

      if (!nextGrid || !hasResultsHeader || nextCards.length === 0) {
        reset();
        return;
      }

      if (nextGrid === currentGrid && sameNodes(cards, nextCards)) return;

      revealOwnedCards();
      cards = nextCards;
      currentGrid = nextGrid;
      currentPage = 1;
      applyPage();
    };

    navigation.addEventListener('click', handleNavigationClick);
    root.addEventListener('submit', handleSearchSubmit, true);

    return {
      destroy() {
        navigation.removeEventListener('click', handleNavigationClick);
        root.removeEventListener('submit', handleSearchSubmit, true);
        reset();
      },
      sync,
    };
  };

  const hasUnmanagedWidget = (root) => {
    const unmanagedHost = document.querySelector(
      '[data-sedna-widget-host]:not(.sedna-search-widget__host)',
    );
    const unmanagedLoader = document.querySelector(
      'script[src*="widget-manual-loader.js"]:not([data-sedna-managed-loader])',
    );

    return Boolean(
      (unmanagedHost && !root.contains(unmanagedHost)) ||
        (unmanagedLoader && !root.contains(unmanagedLoader)),
    );
  };

  const copyLoaderAttribute = (root, loader, attribute) => {
    const value = root.getAttribute(attribute);
    if (value) loader.setAttribute(attribute, value);
  };

  const resetVendorRuntime = () => {
    document.getElementById?.('sedna-manual-widget-js')?.remove();
    document.getElementById?.('sedna-manual-widget-css')?.remove();
    delete window.SednaManualWidget;
    delete window.SednaManualConfig;
  };

  const initializeRoot = (root) => {
    if (!root || root.dataset.sednaInitialized === 'true') return false;

    const host = root.querySelector('.sedna-search-widget__host');
    const resultsHost = root.querySelector('[data-sedna-results-host]');
    const loaderSrc = root.dataset.loaderSrc?.trim();
    const authToken = root.dataset.authToken?.trim();

    if (!host || !loaderSrc || !authToken) return false;

    if (
      (activeRoot && activeRoot !== root && activeRoot.isConnected) ||
      hasUnmanagedWidget(root)
    ) {
      setMessage(
        root,
        root.dataset.conflictMessage || 'Only one Sedna charter search can be used on a page.',
        'conflict',
      );
      return false;
    }

    resetVendorRuntime();
    activeRoot = root;
    root.dataset.sednaInitialized = 'true';
    root.dataset.sednaInboundParams = INBOUND_PARAMS.join(',');
    setMessage(root, '', 'loading');
    normalizeInboundParams();

    const observer = new MutationObserver(() => applyWidgetCopy(root, host));
    observer.observe(host, {
      attributes: true,
      attributeFilter: ['placeholder'],
      childList: true,
      subtree: true,
    });

    let resultsObserver = null;
    let pagination = null;
    if (resultsHost) {
      pagination = createClientPagination(root, resultsHost);
      const syncResults = () => {
        syncResultImages(resultsHost);
        const state = syncResultsState(resultsHost);
        pagination?.sync(state);
      };
      resultsObserver = new MutationObserver(syncResults);
      resultsObserver.observe(resultsHost, {
        attributes: true,
        attributeFilter: ['class', 'src', 'srcset'],
        childList: true,
        subtree: true,
      });
      resultsHost.addEventListener('error', handleResultImageError, true);
      syncResults();
    }

    const loader = document.createElement('script');
    loader.src = loaderSrc;
    loader.async = true;
    loader.setAttribute('data-sedna-managed-loader', 'true');

    LOADER_ATTRIBUTES.forEach((attribute) => copyLoaderAttribute(root, loader, attribute));

    loader.addEventListener('error', () => {
      setMessage(
        root,
        root.dataset.errorMessage || 'Charter search is temporarily unavailable.',
        'error',
      );
    });

    const timeoutId = window.setTimeout(() => {
      if (!host.querySelector('.sedna-search-bar')) {
        setMessage(
          root,
          root.dataset.errorMessage || 'Charter search is temporarily unavailable.',
          'error',
        );
      }
    }, 20000);

    const instance = {
      loader,
      observer,
      pagination,
      resultsHost,
      resultsObserver,
      timeoutId,
    };
    instances.set(root, instance);

    root.appendChild(loader);
    applyWidgetCopy(root, host);
    return true;
  };

  const restoreEditorRoot = (root) => {
    const previousRoot = preservedEditorRoot;
    if (!previousRoot) return false;
    if (root.id !== previousRoot.id && !root.closest('[data-sedna-search-hero]')) return false;

    preservedEditorRoot = null;
    const sameConfiguration = root.id === previousRoot.id &&
      ['data-loader-src', ...LOADER_ATTRIBUTES].every(
        (attribute) => root.getAttribute(attribute) === previousRoot.getAttribute(attribute),
      );

    if (!sameConfiguration) {
      destroy(previousRoot, { preserveVendorRuntime: true, preserveEditorRoot: false });
      return false;
    }

    previousRoot.className = root.className;
    [
      'data-location-placeholder',
      'data-search-button-label',
      'data-conflict-message',
      'data-error-message',
    ].forEach((attribute) => {
      const value = root.getAttribute(attribute);
      if (value === null) previousRoot.removeAttribute(attribute);
      else previousRoot.setAttribute(attribute, value);
    });
    root.replaceWith(previousRoot);
    applyWidgetCopy(previousRoot, previousRoot.querySelector('.sedna-search-widget__host'));
    return true;
  };

  const initialize = (scope = document) => {
    const roots = collectRoots(scope).filter((root) => !restoreEditorRoot(root));
    if (reloadEditorPreviewIfRequired()) return;
    roots.forEach(initializeRoot);
  };

  const destroy = (scope, options = {}) => {
    const preserveVendorRuntime = options.preserveVendorRuntime === true;

    collectRoots(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;

      if (
        preserveVendorRuntime &&
        options.preserveEditorRoot !== false &&
        window.Shopify?.designMode &&
        root.id &&
        root.closest('[data-sedna-search-hero]') &&
        !instance.resultsHost
      ) {
        preservedEditorRoot = root;
        return;
      }

      if (preservedEditorRoot === root) preservedEditorRoot = null;
      instance.observer.disconnect();
      instance.resultsObserver?.disconnect();
      instance.pagination?.destroy();
      instance.resultsHost?.removeEventListener(
        'error',
        handleResultImageError,
        true,
      );
      if (instance.timeoutId) window.clearTimeout(instance.timeoutId);
      instance.loader.remove();
      instances.delete(root);

      delete root.dataset.sednaInitialized;
      delete root.dataset.sednaStatus;
      root.removeAttribute('aria-busy');

      if (activeRoot === root) {
        activeRoot = null;
        if (preserveVendorRuntime) requiresFreshDocument = true;
        else resetVendorRuntime();
      }
    });
  };

  const reloadEditorPreviewIfRequired = () => {
    if (!requiresFreshDocument || !window.Shopify?.designMode) return false;

    if (!previewReloadRequested) {
      previewReloadRequested = true;
      window.location.reload();
    }

    return true;
  };

  const runtime = Object.freeze({
    destroy,
    inboundParams: INBOUND_PARAMS,
    initialize,
    normalizeInboundParams,
    resetVendorRuntime,
  });

  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: false,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) =>
    destroy(event.target, { preserveVendorRuntime: true }),
  );
  window.addEventListener('pageshow', () => initialize(document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
})();
